import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { formatINR } from '../../data/products';
import { 
  Repeat, 
  Scale, 
  Sparkles, 
  Printer, 
  FileText, 
  CheckCircle2, 
  AlertCircle, 
  DollarSign, 
  ShieldCheck, 
  User, 
  Plus, 
  History, 
  ArrowRight 
} from 'lucide-react';

export default function AdminOldGold() {
  const { liveGoldRates, addToast } = useShop();

  // Exchange Valuation Form
  const [customerName, setCustomerName] = useState('Smt. Anjali Mehra');
  const [customerMobile, setCustomerMobile] = useState('+91 98290 44556');
  const [panAadhaar, setPanAadhaar] = useState('ABCDE1234F'); // KYC compliance for gold purchase
  const [ornamentType, setOrnamentType] = useState('Old Traditional Gold Bangle (Pair)');
  
  const [grossWeight, setGrossWeight] = useState(32.4);
  const [stoneDustDeduction, setStoneDustDeduction] = useState(1.4); // Stones, dust, wax (lac)
  const [touchPurity, setTouchPurity] = useState(88.5); // 88.5% touch (approx 21.2KT)
  const [testingMethod, setTestingMethod] = useState('XRF Caratmeter Electronic Assay');
  
  // Buyback rate (Benchmark 24KT Spot minus refinery/melting loss margin)
  const base24KRate = Number(liveGoldRates?.gold24K) || 7380;
  const [meltingMarginPerGram, setMeltingMarginPerGram] = useState(120); // standard refinery fee

  // Vouchers history
  const [vouchers, setVouchers] = useState([
    {
      id: 'OGE-2026-101',
      date: '2026-10-09',
      customer: 'Rameshwar Lal Soni',
      item: 'Old 22KT Chain & Coin',
      grossWt: 24.5,
      netPureWt: 21.8,
      valuation: 158500,
      adjustedInvoice: 'ORD-9021',
      status: 'Adjusted in Invoice'
    },
    {
      id: 'OGE-2026-102',
      date: '2026-10-07',
      customer: 'Kamla Devi Shekhawat',
      item: 'Antique Polki Haar Base',
      grossWt: 45.2,
      netPureWt: 36.4,
      valuation: 264200,
      adjustedInvoice: 'ORD-9018',
      status: 'Adjusted in Invoice'
    }
  ]);

  // Mathematics
  const netMeltWeight = Math.max(0, grossWeight - stoneDustDeduction);
  // Pure 24KT equivalent grams = Net weight * (touch% / 100)
  const fineGoldGrams = netMeltWeight * (touchPurity / 100);
  const effectiveBuybackRate = Math.max(0, base24KRate - meltingMarginPerGram);
  const totalValuation = Math.round(fineGoldGrams * effectiveBuybackRate);

  const handleCreateVoucher = (e) => {
    e.preventDefault();
    const newVoucher = {
      id: `OGE-2026-${100 + vouchers.length + 1}`,
      date: new Date().toISOString().slice(0, 10),
      customer: customerName,
      item: ornamentType,
      grossWt: grossWeight,
      netPureWt: parseFloat(fineGoldGrams.toFixed(2)),
      valuation: totalValuation,
      adjustedInvoice: 'Pending Counter Billing',
      status: 'Voucher Active (Valid 48 Hrs)'
    };
    setVouchers([newVoucher, ...vouchers]);
    addToast?.(`Old Gold Valuation Voucher #${newVoucher.id} for ${formatINR(totalValuation)} generated successfully`, 'success');
  };

  return (
    <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
      
      {/* Top Banner */}
      <div style={{
        background: '#ffffff',
        borderRadius: '8px',
        padding: '1.25rem 1.6rem',
        border: '1px solid #e0e0e0',
        marginBottom: '1.5rem',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#2874f0', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '0.2rem' }}>
            <Repeat size={14} />
            <span>EXCHANGE VALUATION & REFINERY BUYBACK</span>
          </div>
          <h2 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#212121', margin: 0 }}>
            Old Gold Exchange & Caratmeter Testing
          </h2>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#616161' }}>
            Accurate karatage assay, wax & dust deduction, live buyback valuation and invoice adjustment credit vouchers.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.6rem' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '0.45rem 0.8rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700, color: '#1d4ed8' }}>
            <Sparkles size={14} />
            <span>24K Benchmark: ₹{base24KRate}/g</span>
          </div>
        </div>
      </div>

      {/* Main Two Column Workspace */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1.3fr) minmax(320px, 1fr)', gap: '1.5rem', alignItems: 'start' }}>
        
        {/* Left: Input Form */}
        <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.4rem', border: '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1e293b', margin: '0 0 1.2rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Scale size={18} color="#2874f0" />
            <span>Customer Ornament Testing Details</span>
          </h3>

          <form onSubmit={handleCreateVoucher} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '0.8rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Customer Full Name *</label>
                <input
                  type="text"
                  required
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Mobile (+91) *</label>
                <input
                  type="text"
                  required
                  value={customerMobile}
                  onChange={(e) => setCustomerMobile(e.target.value)}
                  style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '0.8rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Ornament Description</label>
                <input
                  type="text"
                  value={ornamentType}
                  onChange={(e) => setOrnamentType(e.target.value)}
                  style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Customer PAN / Aadhaar (KYC)</label>
                <input
                  type="text"
                  value={panAadhaar}
                  onChange={(e) => setPanAadhaar(e.target.value.toUpperCase())}
                  style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', textTransform: 'uppercase' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.8rem', background: '#f8fafc', padding: '1rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Gross Wt (Grams)</label>
                <input
                  type="number"
                  step="0.01"
                  value={grossWeight}
                  onChange={(e) => setGrossWeight(parseFloat(e.target.value) || 0)}
                  style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.9rem', fontWeight: 700 }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#dc2626', marginBottom: '0.3rem' }}>Wax / Lac / Stone (g)</label>
                <input
                  type="number"
                  step="0.01"
                  value={stoneDustDeduction}
                  onChange={(e) => setStoneDustDeduction(parseFloat(e.target.value) || 0)}
                  style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.9rem', fontWeight: 700, color: '#dc2626' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#15803d', marginBottom: '0.3rem' }}>Net Melt Wt (g)</label>
                <input
                  type="number"
                  readOnly
                  value={netMeltWeight.toFixed(2)}
                  style={{ width: '100%', padding: '0.55rem', border: '2px solid #86efac', background: '#f0fdf4', borderRadius: '4px', fontSize: '0.95rem', fontWeight: 900, color: '#166534' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '0.8rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Karatage / Touch Purity (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={touchPurity}
                  onChange={(e) => setTouchPurity(parseFloat(e.target.value) || 0)}
                  style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.9rem', fontWeight: 700 }}
                />
                <span style={{ fontSize: '0.7rem', color: '#64748b' }}>e.g. 91.6% (22KT) | 88.5% (21.2KT) | 75.0% (18KT)</span>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: '0.3rem' }}>Testing Method</label>
                <select
                  value={testingMethod}
                  onChange={(e) => setTestingMethod(e.target.value)}
                  style={{ width: '100%', padding: '0.55rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem' }}
                >
                  <option value="XRF Caratmeter Electronic Assay">XRF Caratmeter Assay</option>
                  <option value="Touchstone Acid Nitric Test">Touchstone Acid Test</option>
                  <option value="Government Approved Assay Lab">Government Assay Lab</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              style={{
                marginTop: '0.5rem',
                padding: '0.75rem',
                borderRadius: '4px',
                border: 'none',
                background: '#2874f0',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.4rem'
              }}
            >
              <FileText size={16} />
              <span>Generate Exchange Valuation Voucher</span>
            </button>
          </form>
        </div>

        {/* Right: Valuation Certificate Preview */}
        <div>
          <div style={{ background: '#ffffff', borderRadius: '8px', border: '2px solid #15803d', overflow: 'hidden', boxShadow: '0 8px 24px rgba(21, 128, 61, 0.12)' }}>
            <div style={{ background: '#15803d', color: '#fff', padding: '1rem 1.3rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', opacity: 0.9 }}>M.L. JEWELLERS JAIPUR</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 900 }}>Old Gold Buyback Voucher</div>
              </div>
              <ShieldCheck size={26} color="#ffe500" />
            </div>

            <div style={{ padding: '1.3rem' }}>
              <div style={{ marginBottom: '1rem', borderBottom: '1px dashed #e2e8f0', paddingBottom: '0.8rem' }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Customer Name</div>
                <div style={{ fontWeight: 800, color: '#1e293b', fontSize: '0.95rem' }}>{customerName}</div>
                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>KYC Document: {panAadhaar}</div>
              </div>

              <table style={{ width: '100%', fontSize: '0.84rem', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '0.4rem 0', color: '#64748b' }}>Gross Ornament Weight:</td>
                    <td style={{ padding: '0.4rem 0', textAlign: 'right', fontWeight: 700 }}>{grossWeight.toFixed(2)} g</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.4rem 0', color: '#64748b' }}>Stone / Dust Deduction:</td>
                    <td style={{ padding: '0.4rem 0', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>-{stoneDustDeduction.toFixed(2)} g</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.4rem 0', color: '#64748b' }}>Net Melt Weight:</td>
                    <td style={{ padding: '0.4rem 0', textAlign: 'right', fontWeight: 700 }}>{netMeltWeight.toFixed(2)} g</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.4rem 0', color: '#64748b' }}>Caratmeter Touch Purity:</td>
                    <td style={{ padding: '0.4rem 0', textAlign: 'right', fontWeight: 800, color: '#2563eb' }}>{touchPurity}%</td>
                  </tr>
                  <tr style={{ borderTop: '1px dashed #cbd5e1' }}>
                    <td style={{ padding: '0.5rem 0', color: '#334155', fontWeight: 700 }}>Calculated 24KT Pure Metal:</td>
                    <td style={{ padding: '0.5rem 0', textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>{fineGoldGrams.toFixed(2)} g</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.4rem 0', color: '#64748b' }}>Effective Buyback Rate:</td>
                    <td style={{ padding: '0.4rem 0', textAlign: 'right', fontWeight: 700 }}>₹{effectiveBuybackRate}/g</td>
                  </tr>
                </tbody>
              </table>

              <div style={{ marginTop: '1.2rem', padding: '1rem', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #86efac', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#15803d', textTransform: 'uppercase' }}>TOTAL EXCHANGE VALUATION</div>
                  <div style={{ fontSize: '0.72rem', color: '#166534' }}>Instant Adjustment towards new jewellery</div>
                </div>
                <div style={{ fontSize: '1.65rem', fontWeight: 900, color: '#14532d' }}>
                  {formatINR(totalValuation)}
                </div>
              </div>

              <button
                onClick={() => {
                  window.print();
                  addToast?.('Exchange voucher printed', 'success');
                }}
                style={{
                  width: '100%',
                  marginTop: '1.2rem',
                  padding: '0.65rem',
                  borderRadius: '4px',
                  border: '1px solid #cbd5e1',
                  background: '#f8fafc',
                  color: '#334155',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4rem'
                }}
              >
                <Printer size={15} />
                <span>Print Official Valuation Slip</span>
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* Recent Exchange History */}
      <div style={{ marginTop: '2rem', background: '#ffffff', borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid #f0f0f0', fontWeight: 800, color: '#1e293b', fontSize: '0.95rem' }}>
          Recent Old Gold Adjustment Vouchers
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '0.74rem', textTransform: 'uppercase' }}>
              <th style={{ padding: '0.75rem 1rem' }}>Voucher No</th>
              <th style={{ padding: '0.75rem 1rem' }}>Client</th>
              <th style={{ padding: '0.75rem 1rem' }}>Ornament Description</th>
              <th style={{ padding: '0.75rem 1rem' }}>Gross Wt</th>
              <th style={{ padding: '0.75rem 1rem' }}>Fine 24K Wt</th>
              <th style={{ padding: '0.75rem 1rem' }}>Valuation (INR)</th>
              <th style={{ padding: '0.75rem 1rem' }}>Adjustment Status</th>
            </tr>
          </thead>
          <tbody>
            {vouchers.map(v => (
              <tr key={v.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '0.75rem 1rem', fontWeight: 700, color: '#2874f0' }}>{v.id}</td>
                <td style={{ padding: '0.75rem 1rem', fontWeight: 700, color: '#1e293b' }}>{v.customer}</td>
                <td style={{ padding: '0.75rem 1rem', color: '#475569' }}>{v.item}</td>
                <td style={{ padding: '0.75rem 1rem' }}>{v.grossWt}g</td>
                <td style={{ padding: '0.75rem 1rem', fontWeight: 700, color: '#065f46' }}>{v.netPureWt}g</td>
                <td style={{ padding: '0.75rem 1rem', fontWeight: 800, color: '#15803d' }}>{formatINR(v.valuation)}</td>
                <td style={{ padding: '0.75rem 1rem' }}>
                  <span style={{ background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', padding: '0.15rem 0.5rem', borderRadius: '3px', fontWeight: 700, fontSize: '0.7rem' }}>
                    {v.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}
